import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import Button from '../components/ui/Button';
import ProductCard from '../components/ui/ProductCard';
import MaterialStory from '../components/sections/MaterialStory';
import ReviewGrid from '../components/sections/ReviewGrid';
import TrustBadges from '../components/common/TrustBadges';
import {products} from '../data/storeData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function Home() {
  const featured = products.slice(0, 4);
  
  return (
    <div className="min-h-screen pt-24 md:pt-0">
      {/* Hero Section */}
      <section className="relative h-[100vh] min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1544465544-1b71aee9dfa3?auto=format&fit=crop&q=80&w=1920" alt="Leather workshop" className="w-full h-full object-cover object-center scale-105 animate-slow-zoom" />
          <div className="absolute inset-0 bg-navy/60 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-transparent to-navy/90"></div>
        </div>
        <motion.div 
          initial={{opacity: 0, y: 30}} 
          animate={{opacity: 1, y: 0}} 
          transition={{duration: 1.2, ease: "easeOut"}}
          className="relative z-10 text-center px-4 max-w-5xl"
        >
          <motion.div 
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            transition={{delay: 0.4}}
            className="mb-10 inline-block"
          >
            <span className="font-sans text-[10px] tracking-[0.6em] uppercase text-brass bg-navy/40 px-6 py-3 border border-brass/30 backdrop-blur-lg">
              Est. 2018 | Workshop Goods
            </span>
          </motion.div>
          <h1 className="font-serif text-6xl md:text-9xl text-cream mb-10 leading-[1] tracking-tight">
            Built for the <br/>
            <span className="text-brass italic">Daily Journey</span>
          </h1>
          <p className="font-sans text-cream/70 text-lg md:text-2xl mb-14 max-w-3xl mx-auto font-light leading-relaxed">
            Premium full-grain leather goods handcrafted to age beautifully and develop a unique soul with every mile travelled.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/shop">
              <Button variant="accent" className="px-12 py-6 text-[11px] shadow-2xl">Shop the Collection</Button>
            </Link>
            <Link to="/builder" className="group flex items-center gap-4 font-sans text-[11px] tracking-[0.4em] uppercase text-cream hover:text-brass transition-colors">
              Builder Experience
              <span className="w-10 h-px bg-cream group-hover:bg-brass transition-all group-hover:w-16"></span>
            </Link>
          </div>
        </motion.div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4">
          <span className="font-sans text-[8px] uppercase tracking-[0.4em] text-cream/40 vertical-text">Explore</span>
          <div className="w-px h-20 bg-gradient-to-b from-brass to-transparent"></div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-40 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div className="max-w-xl">
            <h4 className="font-sans text-[11px] tracking-[0.4em] uppercase text-saddle mb-4 font-bold">Curated Carry</h4>
            <h2 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Workshop Staples</h2>
          </div>
          <Link to="/shop" className="font-sans text-[11px] tracking-[0.3em] uppercase text-navy hover:text-saddle transition-all border-b border-navy/20 hover:border-saddle pb-2 group">
            Browse Entire Collection
            <SafeIcon icon={FiIcons.FiArrowRight} className="inline ml-2 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
          {featured.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* Trust & Services */}
      <section className="bg-creamDark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrustBadges />
        </div>
      </section>

      <MaterialStory />
      
      <ReviewGrid />

      {/* Newsletter / CTA */}
      <section className="py-32 bg-navy relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-brass/5 skew-x-12 transform translate-x-1/4"></div>
        <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
          <h2 className="font-serif text-5xl text-cream mb-8">Join the Workshop Journal</h2>
          <p className="font-sans text-cream/50 mb-14 tracking-[0.2em] uppercase text-[10px] max-w-lg mx-auto leading-loose">
            Access limited batch releases, leather care guides, and artisanal stories delivered to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-0 max-w-xl mx-auto border border-cream/20" onSubmit={(e) => e.preventDefault()}>
            <input 
              type="email" 
              placeholder="ARTISAN@HARBORHIDE.COM" 
              className="flex-grow bg-white/5 px-8 py-5 text-cream font-sans text-xs tracking-widest focus:outline-none focus:bg-white/10 transition-all"
            />
            <button className="bg-brass text-navy px-10 py-5 font-sans text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-cream transition-colors">
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}